import { useState, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import BarChartNew, {
  type BarChartGranularity
} from '../BarChartNew/BarChartNew'
import LineChartNew from '../LineChartNew/LineChartNew'
import Switch from '../Switch/Switch'
import ChartHeader from '../ChartHeader/ChartHeader'
import { type SwitchOption } from '../Switch/types'
import useFunctionMemo from '../../hooks/use_function_memo'
import getChartData from '../../utils/data/get_chart_data'
import getEmissionsDataMerged from '../../utils/data/get_emissions_data_merged'
import getEmissionsTotalArea from '../../utils/data/get_emissions_total_area'
import { type Emissions, type ChartDataPoint } from 'types'
import { DataType } from 'types/data'
import { generateChartCSV } from '../../csv'
import downloadCSV from '../../utils/download_csv'
import exportElementToPNG from '../../utils/export_element_to_png'
import exportElementToJPG from '../../utils/export_element_to_jpg'

export enum ChartType {
  BAR = 'Bar',
  LINE = 'Line'
}

type Props = {
  title: string
  emissions: Emissions[]
  granularity: BarChartGranularity
  variant?: ChartType
}

export default function NetEmissionsChartNew({
  title,
  emissions,
  granularity,
  variant = ChartType.BAR
}: Props) {
  const [dataType, setDataType] = useState<DataType>(DataType.NEE)
  const { t } = useTranslation()
  const containerRef = useRef<any>(null)
  const mergedEmissions = useFunctionMemo(getEmissionsDataMerged, emissions)
  const mergedEmissionsArea = useFunctionMemo(getEmissionsTotalArea, emissions)

  const exportNameColumnLabel = t('export.column_name')
  const exportNEEColumnLabel =
    dataType === DataType.NEE
      ? t('export.column_nee')
      : t('export.column_nee_intensity')
  const exportColumnLabels = useMemo(
    (): string[] => [exportNameColumnLabel, exportNEEColumnLabel],
    [exportNameColumnLabel, exportNEEColumnLabel]
  )

  const singleEmissionsChartData = useFunctionMemo<ChartDataPoint[]>(
    getChartData,
    mergedEmissions,
    dataType,
    mergedEmissionsArea
  )

  const multipleEmissionsChartData = useFunctionMemo<ChartDataPoint[]>(
    getChartData,
    emissions,
    dataType
  )

  const dataTypeSwitchOptionA = useMemo(
    (): SwitchOption => ({
      label: t('net_emissions_chart.data_type_switch_option_nee'),
      value: DataType.NEE
    }),
    [t]
  )

  const dataTypeSwitchOptionB = useMemo(
    (): SwitchOption => ({
      label: t('net_emissions_chart.data_type_switch_option_intensity'),
      value: DataType.Intensity
    }),
    [t]
  )

  const onExportCSV = useCallback((): void => {
    const csv = generateChartCSV({
      data:
        variant === ChartType.LINE
          ? multipleEmissionsChartData
          : singleEmissionsChartData,
      emissions,
      ...(variant === ChartType.BAR
        ? { columnLabels: exportColumnLabels }
        : {}),
      chartTitle: title,
      t
    })

    const csvFilename =
      variant === ChartType.LINE && multipleEmissionsChartData.length > 0
        ? `${t('export.groups_file_name_prefix')} ${multipleEmissionsChartData.length} ${t(
            'export.groups_file_name_suffix'
          )}`
        : t('export.file_name')

    downloadCSV(csv, csvFilename)
  }, [emissions, exportColumnLabels])

  const onExportPNG = useCallback(() => {
    const { current } = containerRef

    // eslint-disable-next-line github/no-then,@typescript-eslint/no-unsafe-argument
    exportElementToPNG(current, 400, 'result.png').catch((err: Error) => {
      console.error(err)
    })
  }, [containerRef.current])

  const onExportJPG = useCallback(() => {
    const { current } = containerRef

    // eslint-disable-next-line github/no-then,@typescript-eslint/no-unsafe-argument
    exportElementToJPG(current, 400, 'result.jpg').catch((err: Error) => {
      console.error(err)
    })
  }, [containerRef.current])

  const labelY =
    dataType === DataType.NEE
      ? t('net_emissions_chart.label_y_nee')
      : t('net_emissions_chart.label_y_intensity')

  return (
    <div ref={containerRef}>
      <ChartHeader
        title={title}
        onExportCSV={onExportCSV}
        onExportPNG={onExportPNG}
        onExportJPG={onExportJPG}
        controls={
          <Switch
            value={dataType}
            onChange={setDataType}
            optionA={dataTypeSwitchOptionA}
            optionB={dataTypeSwitchOptionB}
          />
        }
      />
      {variant === ChartType.BAR && (
        <BarChartNew
          data={singleEmissionsChartData}
          labelX={t('net_emissions_chart.label_x')}
          labelY={labelY}
          granularity={granularity}
          dataType={dataType}
        />
      )}
      {variant === ChartType.LINE && (
        <LineChartNew
          data={multipleEmissionsChartData}
          labelX={t('net_emissions_chart.label_x')}
          labelY={labelY}
          emissions={emissions}
          dataType={dataType}
        />
      )}
    </div>
  )
}
